import Swatches from './Swatches';
import { on, off, trigger } from 'toolbox/event';
import { deepMerge } from 'toolbox/deepMerge';

/**
 * This is a description of the SwatchesGrouped constructor function.
 * @class
 * @classdesc This is a description of the SwatchesGrouped class.
 * @extends Component
 */
export default class SwatchesGrouped extends Swatches {
    /**
     * @typedef {Object} optionsObject
     * @property {String} dataModelId - the data model name needed to render the component on client side.
     * @property {Boolean} reloadOnProductUpdate - if true, this component will be reloaded on "product.updated" event.
     *                                             Please refer product/Product component for more info.
     * @property {Boolean} changeImageOnHover - if true, then trigger an event on hover on swatch to switch product image
     * @property {Object} classNames
     */

    /**
     * @constructor
     * @param {HTMLElement} element Component DOM node
     * @param {optionsObject} options - Component Options
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            templateName: 'product/swatchesgrouped',
            reloadOnProductUpdate: true,
            changeImageOnHover: true,
            changeImageOnSelectSwatch: true,
            dataModelId: 'swatchesgrouped',
            viewAllSwatchesThreshold: 24, // count of swatches that will be shown in case if option showViewAll is true
            showViewAll: false, // add `View all` button to show/hide swatches based on viewAllSwatchesThreshold option
            classNames: {
                selected: 'm-selected',
                hidden: 'h-hidden',
            },
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        super.initCache();
        this.selectors.swatchesGroup = this.element.querySelectorAll('[data-js-swatches-grouped-panel]');
        this.selectors.swatchesToggler = this.element.querySelectorAll('[data-js-swatches-grouped-view-all]');
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        super.initState();
        this.state.selectedTabID = this.options.selectedTab;
        this.state.isSwatchesVisible = false;
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    bindEvents() {
        super.bindEvents();
        on('tab.opened', this.element, this.onTabChanged.bind(this));
        if (this.options.showViewAll && this.selectors.swatchesToggler) {
            on('click', this.selectors.swatchesToggler, this.onSwatchTogglerClick.bind(this));
        }
    }

    /**
     * Click event handler
     * @param {Object} event - Event object
     */
    onSwatchTogglerClick(event) {
        const targetElement = event.currentTarget;
        const swatches = targetElement.closest('[data-js-swatches-grouped-panel]').querySelectorAll('[data-js-swatch]');
        if (swatches) {
            if (this.state.isSwatchesVisible) {
                targetElement.classList.remove('m-active');
                targetElement.setAttribute('aria-expanded', 'false');
                this.hideSwatches(swatches);
            } else {
                targetElement.classList.add('m-active');
                targetElement.setAttribute('aria-expanded', 'true');
                this.showSwatches(swatches);
            }
            trigger('accordion.update', this.element, { bubbles: true });
        }
    }

    /**
     * Hide swatches
     * @param {Array} swatches - Swatches list to hide
     */
    hideSwatches(swatches) {
        if (!swatches) {
            return;
        }
        this.state.isSwatchesVisible = false;
        swatches.forEach((swatch, index) => {
            if (index >= this.options.viewAllSwatchesThreshold) {
                swatch.classList.add(this.classes.hidden);
            }
        });
    }

    /**
     * Show swatches
     * @param {Array} swatches - Swatches list to hide
     */
    showSwatches(swatches) {
        if (!swatches) {
            return;
        }
        this.state.isSwatchesVisible = true;
        swatches.forEach((swatch, index) => {
            if (index >= this.options.viewAllSwatchesThreshold) {
                swatch.classList.remove(this.classes.hidden);
            }
        });
    }

    /**
     * Updates state on tab opened event.
     *
     * @param {Object} e - Event object
     */
    onTabChanged(e) {
        this.state.selectedTabID = e.detail.tabID;
    }

    /**
     * Get varaition data model
     * @param {Object} data - data
     * @returns {Object} data
     */
    getVariationModel(data) {
        return this.getDataForRendering(data);
    }

    /**
     * Returns modified data model to reload component with saved state.
     *
     * @param {Object} data original data model for template rendering
     * @returns {Promise} data
     */
    getDataForRendering(data) {
        data.hidden = this.element.classList.contains(this.options.classNames.hidden);
        data.showToggle = false;
        data.dataOptions.selectedTab = this.state.selectedTabID;
        Object.keys(data.groupedSwatches).forEach((tabID) => {
            data.groupedSwatches[tabID].isSelected = tabID === this.state.selectedTabID;
        });
        return data;
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        super.destroy();
        off('tab.opened', this.element);
    }
}
